import {GeneralStatusEnum} from "../../enums/GeneralStatusEnum";
import {PlanLevelEnum} from "../../enums/PlanLevelEnum";
import {FeatureTypeEnum} from "../../enums/FeatureTypeEnum";
import {FeatureStatusEnum} from "../../enums/FeatureStatusEnum";
import {PhotoProcessingStatusEnum} from "../../enums/PhotoProcessingStatusEnum";
import {RecognitionIndexStatusEnum} from "../../enums/RecognitionIndexStatusEnum";

enum EntityTag {
    Organisation = 'ORGANISATION',
    Event = 'EVENT',
    Gallery = 'GALLERY',
    Photo = 'PHOTO',
    SystemSettings = 'SYSTEM_SETTINGS',
}

interface IAuth {
    access_token: string;
}

interface IOrganisation {
    id: number;
    name: string;
    organisationFeaturesDto?: string;
    plan: PlanLevelEnum;
}

interface IApiUser {
    id: number;
    email: string;
    roles: string;
    status: GeneralStatusEnum;
    organisation: IOrganisation;
}

interface IUpdateApiUser {
    id: number;
    email: string;
    roles: string;
    status: GeneralStatusEnum;
}

interface ICreateApiUser {
    email: string;
    roles: string;
}

interface ICreateOrganisationWithApiUser {
    organisationName: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface IFeatureSpecification {
    status: FeatureStatusEnum;
    value: any;
    unlockLevel: PlanLevelEnum;
}

interface IFeature {
    name: FeatureTypeEnum,
    specification: IFeatureSpecification;
}

interface IOrganisationFeatures {
    features: IFeature[];
}

interface IEvent {
    id: number;
    hash: string;
    name: string;
    description?: string;
    date: string;
    location?: string;
    organisationId: number;
    status: GeneralStatusEnum;
    organisation?: IOrganisation;
    recognitionIndexStatus?: RecognitionIndexStatusEnum;
    recognitionIndexProgress?: number;
    recognitionIndexTotalItems?: number;
    recognitionIndexProcessedItems?: number;
    recognitionIndexStartedAt?: string;
    recognitionIndexCompletedAt?: string;
    recognitionIndexError?: string;
    recognitionNeedsReindex?: boolean;
}

interface IEventAnalytics {
    id: number;
    eventId: number;
    eventViews: number;
    selfieUploads: number;
    selfieMatches: number;
    photoDownloads: number;
    bulkDownloads: number;
    createdAt: string;
    updatedAt: string;
}

interface IMatchingPhoto {
    itemId: number;
    itemHash: string;
    similarityScore: number;
    signedUrl: string;
    thumbnailUrl?: string;
    downloadUrl: string;
    metadata: any;
}

interface IIndexingStatus {
    eventId: number;
    status: RecognitionIndexStatusEnum;
    progress: number;
    totalItems: number;
    processedItems: number;
    startedAt?: string;
    completedAt?: string;
    error?: string;
    needsReindex: boolean;
}

interface ICollectionStats {
    collectionId: string;
    exists: boolean;
    faceCount?: number;
    createdAt?: string;
}

interface IGallery {
    id: number;
    name: string;
    description?: string;
    eventId: number;
    type: string;
    hash: string;
    createdAt: string;
    updatedAt: string;
    event: IEvent;
    excludeFromRecognition?: boolean;
    isPubliclyVisible?: boolean;
}

interface ICreateGallery {
    name: string;
    description?: string;
    eventId: number;
    type: string;
}

interface IGalleryPhoto {
    id: number;
    galleryId: number;
    filename: string;
    url: string;
    thumbnailUrl: string;
    fileSize: number;
    mimeType: string;
    width: number;
    height: number;
    status: GeneralStatusEnum | PhotoProcessingStatusEnum;
    createdAt: string;
    updatedAt: string;
}

// For upload progress tracking
type RcFile = File & {
    uid: string;
    lastModifiedDate: Date;
};

interface ISystemSetting {
    id: number;
    name: string;
    value: string;
    type: string;
    createdAt: string;
    updatedAt: string;
}

export {
    EntityTag,
};

export type {
    IAuth,
    IApiUser,
    IUpdateApiUser,
    ICreateApiUser,
    ICreateOrganisationWithApiUser,
    IOrganisation,
    IFeatureSpecification,
    IFeature,
    IOrganisationFeatures,
    IEvent,
    IEventAnalytics,
    IGallery,
    ICreateGallery,
    IGalleryPhoto,
    RcFile,
    IMatchingPhoto,
    IIndexingStatus,
    ICollectionStats,
    ISystemSetting,
}

export { PhotoProcessingStatusEnum };
